import Vue from 'vue'
import App from './App.vue'
import VueApollo from 'vue-apollo'
import VueRouter from 'vue-router'
import { ApolloClient } from 'apollo-client'
import { createHttpLink } from 'apollo-link-http'
import { InMemoryCache } from 'apollo-cache-inmemory'
import { ApolloLink, split } from 'apollo-link'
import { WebSocketLink } from 'apollo-link-ws'
import { getMainDefinition } from 'apollo-utilities'
import { merge } from 'lodash'
import ReactiveProvide from 'vue-reactive-provide'
import router from './routes'
import settings from './settings'
import './components/index'
import { getAccessToken } from './lib/auth'

import './assets/tailwind.css'
import './lib/primevue/init'
import './assets/index.scss'
import './assets/primevue-theme.scss'

const authMiddleware = new ApolloLink((operation, forward) => {
  const token = getAccessToken()
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
  }))
  return forward(operation)
})

// Dev: call API on the SAME hostname as the UI (localhost vs 127.0.0.1).
// Mixing them blanks the dashboard in Chrome (private-network / CORS hangs).
// Prod: same-origin relative paths (Render serves UI + API together).
function resolveApiBases() {
  if (process.env.VUE_APP_API_HTTP) {
    const http = process.env.VUE_APP_API_HTTP
    const ws = process.env.VUE_APP_API_WS
      || http.replace(/^https:/i, 'wss:').replace(/^http:/i, 'ws:')
    return { http, ws }
  }
  if (process.env.NODE_ENV === 'development' && typeof location !== 'undefined') {
    const host = location.hostname || 'localhost'
    return {
      http: `http://${host}:4000`,
      ws: `ws://${host}:4000`,
    }
  }
  const ws = typeof location !== 'undefined'
    ? `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}`
    : ''
  return { http: '', ws }
}

const { http: apiHttpBase, ws: apiWsBase } = resolveApiBases()

const httpLink = authMiddleware.concat(createHttpLink({
  uri: `${apiHttpBase}/graphql`,
}))
const wsLink = new WebSocketLink({
  uri: `${apiWsBase}/subscriptions`,
  options: {
    reconnect: true,
    lazy: true,
    connectionParams: () => {
      const token = getAccessToken()
      return token ? { Authorization: `Bearer ${token}` } : {}
    },
  },
})
const link = split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
  },
  wsLink,
  httpLink
)
const cache = new InMemoryCache()
const apolloClient = new ApolloClient({
  link,
  cache,
})
Vue.use(VueApollo)
const apolloProvider = new VueApollo({
  defaultClient: apolloClient,
})

Vue.use(VueRouter)
Vue.use(ReactiveProvide);

Vue.config.productionTip = false

const defaultSettings = {
  version: '0.0.2',
  useRelativeTime: false,
  cronPage: {
    orderByName: 'userConfig'
  }
}
const savedSettings = settings.getSettings()
const mergedSettings = merge(defaultSettings, savedSettings)
const globalStore = new Vue({
  data: mergedSettings,
  methods: {
    save() {
      const temp = Object.assign({}, globalStore.$data)
      delete temp.$apolloData
      settings.saveSettings(temp)
    },
  },
})
Vue.prototype.$globals = globalStore

new Vue({
  render: h => h(App),
  router,
  apolloProvider,
}).$mount('#app')
